import { useCallback, useEffect, useRef, useState } from 'react'
import { Pencil } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { InfoPopover } from '@/components/ui/InfoPopover'
import applicationStyles from '@/features/application/styles/applicationList.module.css'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import { cn } from '@/lib/utils/helpers'
import { CronEditModal } from '../components/CronEditModal'
import {
  getCronJobs,
  saveCronJob,
  toggleCronStatus,
} from '../services/jobsService'
import type { CronJob, CronListParams, CronSubmitPayload, CronStatus } from '../types'
import {
  cronDescriptionPreview,
  cronListParamsEqual,
  normalizeCronListParams,
} from '../utils/cronFormatters'

function CronDescriptionCell({
  html,
  isLastRow,
}: {
  html: string
  isLastRow: boolean
}) {
  const full = html ?? ''
  const preview = cronDescriptionPreview(full)

  if (!preview) {
    return <td className="overflow-hidden text-slate-700">—</td>
  }

  return (
    <td className="overflow-hidden text-slate-700">
      <InfoPopover
        inline
        contentHtml={full}
        align="start"
        maxWidth={448}
        isLastRow={isLastRow}
        testId="cron-description-popover"
        contentClassName="whitespace-normal text-sm leading-relaxed [&_a]:text-[#1597ff] [&_a]:underline [&_img]:max-w-full [&_p]:my-1"
      >
        <button
          type="button"
          className="block w-full max-w-full cursor-default border-0 bg-transparent p-0 text-left font-inherit text-inherit text-slate-700"
          aria-label="Show full description"
        >
          <span className="block truncate">{preview}</span>
        </button>
      </InfoPopover>
    </td>
  )
}

const SESSION_KEY = 'cronlist_filter'

function loadSessionFilter(): { q?: string; cron_status?: CronStatus | '' } {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return {}
    return JSON.parse(raw) as { q?: string; cron_status?: CronStatus | '' }
  } catch {
    return {}
  }
}

function saveSessionFilter(filter: CronListParams) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(filter))
  } catch {
    // Ignore
  }
}

function clearSessionFilter() {
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    // Ignore
  }
}

export function CronJobsPage() {
  const [jobs, setJobs] = useState<CronJob[]>([])
  const [params, setParams] = useState<CronListParams>({})
  const [searchInput, setSearchInput] = useState('')
  const [statusInput, setStatusInput] = useState<CronStatus | ''>('')
  const [loading, setLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<CronJob | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [toggleTarget, setToggleTarget] = useState<CronJob | null>(null)
  const mountedRef = useRef(false)
  const statusInputRef = useRef<CronStatus | ''>('')
  statusInputRef.current = statusInput

  const load = useCallback(async (next: CronListParams) => {
    setLoading(true)
    saveSessionFilter(next)
    try {
      setJobs(await getCronJobs(next))
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    const saved = loadSessionFilter()
    const initialQ = saved.q ?? ''
    const initialStatus = (saved.cron_status as CronStatus | '') ?? ''
    setSearchInput(initialQ)
    setStatusInput(initialStatus)
    const initialParams = normalizeCronListParams(initialQ, initialStatus)
    setParams(initialParams)
    void load(initialParams)
  }, [load])

  const mergeAndLoad = useCallback(
    (q: string, cronStatus: CronStatus | '') => {
      const next = normalizeCronListParams(q, cronStatus)
      setParams((prev) => {
        if (cronListParamsEqual(prev, next)) return prev
        void load(next)
        return next
      })
    },
    [load],
  )

  /** Search runs while typing (debounced); status updates immediately (Angular Email Templates / Mailtrap parity). */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      mergeAndLoad(searchInput, statusInputRef.current)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [searchInput, mergeAndLoad])

  const applyStatusFilter = (nextStatus: CronStatus | '') => {
    setStatusInput(nextStatus)
    mergeAndLoad(searchInput, nextStatus)
  }

  const submitFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mergeAndLoad(searchInput, statusInput)
  }

  const resetFilter = () => {
    clearSessionFilter()
    setSearchInput('')
    setStatusInput('')
    mergeAndLoad('', '')
  }

  const openAdd = () => {
    setEditTarget(null)
    setEditOpen(true)
  }

  const openEdit = (job: CronJob) => {
    setEditTarget(job)
    setEditOpen(true)
  }

  const handleSubmit = async (payload: CronSubmitPayload, cronId?: string | number) => {
    setSubmitting(true)
    try {
      await saveCronJob(payload, cronId)
      showSuccess('Data Updated Successfully')
      setEditOpen(false)
      setEditTarget(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const confirmToggle = async () => {
    if (!toggleTarget) return
    try {
      await toggleCronStatus(toggleTarget)
      showSuccess('Job Status Updated Successfully')
      setToggleTarget(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    }
  }

  return (
    <PageShell title="CRON JOBS">
      <div className="flex w-full flex-wrap items-center gap-2 sm:flex-nowrap sm:justify-between">
        <form
          onSubmit={submitFilter}
          className="flex min-w-0 flex-1 flex-wrap items-center gap-2"
        >
          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search..."
            className="min-w-[12rem] max-w-md flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff]"
          />
          <select
            value={statusInput}
            onChange={(event) =>
              applyStatusFilter(event.target.value as CronStatus | '')
            }
            className="px-3 py-2 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff]"
          >
            <option value="">All</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
          <button
            type="submit"
            className={primaryEntityActionButtonClass}
          >
            Search
          </button>
          <button
            type="button"
            onClick={resetFilter}
            className={secondaryEntityActionButtonClass}
          >
            Reset
          </button>
        </form>
        <button
          type="button"
          onClick={openAdd}
          className={primaryEntityActionButtonClass}
        >
          Add New Cron
        </button>
      </div>

      <div className="mt-4 flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table
            className={cn(applicationStyles.listingTable, 'min-w-[1100px]')}
            style={{ tableLayout: 'fixed', width: '100%' }}
          >
            <colgroup>
              <col style={{ width: '22%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '32%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Cron ID #</th>
                <th>Cron Name</th>
                <th>Cron Time</th>
                <th>Description</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
          <tbody>
            {loading ? (
              <ListingTableLoadingRow colSpan={6} />
            ) : jobs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  No cron jobs.
                </td>
              </tr>
            ) : (
              jobs.map((job, rowIndex) => (
                <tr key={job.cron_id}>
                  <td className="overflow-hidden break-all text-slate-700">
                    {job.cron_id}
                  </td>
                  <td className="overflow-hidden break-words text-slate-700">
                    {job.cron_name || '—'}
                  </td>
                  <td className="overflow-hidden whitespace-nowrap text-slate-700">
                    {job.cron_time || '—'}
                  </td>
                  <CronDescriptionCell
                    html={job.cron_description ?? ''}
                    isLastRow={rowIndex === jobs.length - 1}
                  />
                  <td className="overflow-hidden whitespace-nowrap">
                    <span
                      className={cn(
                        'inline-flex items-center rounded px-2 py-1 text-xs font-medium',
                        job.cron_status === 'ACTIVE'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-slate-100 text-slate-600',
                      )}
                    >
                      {job.cron_status}
                    </span>
                  </td>
                  <td className="overflow-hidden whitespace-nowrap">
                    <div className="flex flex-nowrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setToggleTarget(job)}
                        className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 transition-colors hover:bg-slate-50"
                        aria-label={
                          job.cron_status === 'ACTIVE'
                            ? 'Switch to inactive'
                            : 'Switch to active'
                        }
                      >
                        {job.cron_status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'}
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(job)}
                        className="shrink-0 rounded p-1.5 text-[#1597ff] hover:bg-[#1597ff]/10"
                        aria-label="Edit"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      <CronEditModal
        isOpen={editOpen}
        cronJob={editTarget}
        submitting={submitting}
        onClose={() => {
          setEditOpen(false)
          setEditTarget(null)
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        isOpen={toggleTarget !== null}
        title="Cron Status"
        message={
          toggleTarget
            ? `update cron status to ${toggleTarget.cron_status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'}`
            : ''
        }
        primaryLabel="Confirm"
        onPrimary={confirmToggle}
        secondaryLabel="Cancel"
        onSecondary={() => setToggleTarget(null)}
      />
    </PageShell>
  )
}
